import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await checkAuth();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) { setStatus("error"); return; }
      } catch (e) { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
  }, [checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream px-6">
      <div className="max-w-md w-full text-center bg-white rounded-3xl border border-brand-border/60 p-10 shadow-[0_8px_30px_rgba(74,93,82,0.10)]" data-testid="payment-success-card">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-brand-primary animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-2">Confirming your payment…</h1>
            <p className="text-brand-slate">This only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-brand-primary mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-2">You're all set!</h1>
            <p className="text-brand-slate mb-6">Your subscription is active. Enjoy unlimited previews.</p>
            <Button data-testid="go-to-studio-btn" onClick={() => navigate("/dashboard")} className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white">Go to Studio</Button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-destructive mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-2">Something went wrong</h1>
            <p className="text-brand-slate mb-6">We couldn't confirm your payment. Please try again.</p>
            <Button onClick={() => navigate("/dashboard")} className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white">Back to Studio</Button>
          </>
        )}
      </div>
    </div>
  );
}
